import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ThemedButton } from '../ThemedButton';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Shield, FileCheck, Users, Search, UserCheck } from 'lucide-react';
import { useWizard } from '../../contexts/WizardContext';

const POLICIES = [
  {
    key: 'has_waiver',
    label: 'Participant Waiver',
    description: 'Do you require participants (or their guardians) to sign a waiver before participating?',
    icon: FileCheck,
  },
  {
    key: 'has_code_of_conduct',
    label: 'Code of Conduct',
    description: 'Does your organization have a written code of conduct for coaches, staff, and volunteers?',
    icon: Users,
  },
  {
    key: 'has_abuse_prevention_policy',
    label: 'Abuse Prevention Policy',
    description: 'Does your organization have a formal abuse prevention and reporting policy in place?',
    icon: Shield,
  },
  {
    key: 'has_background_checks',
    label: 'Background Checks',
    description: 'Does your organization conduct background checks on coaches, staff, and volunteers?',
    icon: Search,
  },
  {
    key: 'has_one_on_one_policy',
    label: '1-on-1 Interaction Policy',
    description: 'Does your organization have a policy governing one-on-one interactions between adults and minors?',
    icon: UserCheck,
  },
];

const SafetyPolicies = () => {
  const { formData, updateFormData, nextStep } = useWizard();

  const { control, handleSubmit } = useForm({
    defaultValues: {
      has_waiver: formData.has_waiver ?? null,
      has_code_of_conduct: formData.has_code_of_conduct ?? null,
      has_abuse_prevention_policy: formData.has_abuse_prevention_policy ?? null,
      has_background_checks: formData.has_background_checks ?? null,
      has_one_on_one_policy: formData.has_one_on_one_policy ?? null,
    },
  });

  const onSubmit = (data) => {
    updateFormData(data);
    nextStep();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <p className="text-sm text-[#6B7280] mb-6">
          These safety measures help us understand your organization's risk profile and may positively impact your coverage options.
        </p>
      </div>

      <div className="space-y-4">
        {POLICIES.map(({ key, label, description, icon: Icon }) => (
          <Controller
            key={key}
            name={key}
            control={control}
            rules={{ validate: (v) => v !== null || `Please answer whether you have a ${label.toLowerCase()}` }}
            render={({ field, fieldState }) => (
              <div
                className={`border rounded-xl p-5 transition-colors ${
                  fieldState.error ? 'border-red-300 bg-red-50/30' : 'border-[#E5E7EB] bg-white'
                }`}
                data-testid={`policy-card-${key}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#EEF0FF] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon className="h-5 w-5 text-[#7B6FE8]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div>
                        <p className="font-semibold text-[#1A1A1A] text-sm">{label}</p>
                        <p className="text-xs text-[#6B7280] mt-0.5">{description}</p>
                      </div>
                      <RadioGroup
                        onValueChange={(value) => field.onChange(value === 'true')}
                        value={field.value === null ? undefined : String(field.value)}
                        className="flex gap-4 shrink-0"
                      >
                        <div className="flex items-center gap-1.5">
                          <RadioGroupItem value="true" id={`${key}_yes`} data-testid={`${key}-yes`} />
                          <Label htmlFor={`${key}_yes`} className="font-normal cursor-pointer text-sm">Yes</Label>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <RadioGroupItem value="false" id={`${key}_no`} data-testid={`${key}-no`} />
                          <Label htmlFor={`${key}_no`} className="font-normal cursor-pointer text-sm">No</Label>
                        </div>
                      </RadioGroup>
                    </div>
                    {fieldState.error && (
                      <p className="text-xs text-red-500 mt-1">{fieldState.error.message}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          />
        ))}
      </div>

      <ThemedButton
        type="submit"
        data-testid="safety-policies-next-btn"
        variant="wizard"
        size="full"
      >
        Next: Sports & Participants
      </ThemedButton>
    </form>
  );
};

export default SafetyPolicies;
